import React, { useEffect, useState, useRef } from 'react';
import { db, auth } from '../firebaseConfig'; 
import { collection, doc, onSnapshot, query, updateDoc, orderBy, getDoc, getDocs, where } from "firebase/firestore"; 
import { signOut } from "firebase/auth"; 
import { getFunctions, httpsCallable } from "firebase/functions"; 
import { QRCodeSVG } from 'qrcode.react'; 
import ClientAudience from './ClientAudience'; 
import SocialAutopilot from './SocialAutopilot';
import { Users } from 'lucide-react'; 
import AnalyticsDashboard from './AnalyticsDashboard'; // 👈 YE NAYI LINE ADD KAREIN
import { 
  BarChart3, TrendingUp, Save, Bot, MessageSquare, LogOut, 
  Download, Zap, Search, ShieldCheck, Database, Server, CheckCircle2, AlertTriangle, 
  Settings, Battery, BrainCircuit, QrCode, Store, Copy, ShoppingBag, 
  Wand2, CreditCard, ArrowUpRight, X, Crown, Check, Power, Filter, ArrowUpDown, Menu, Bell,
  Crosshair, Calendar, ImageIcon, Clock, Edit2, ExternalLink, Rocket, Share2 
} from 'lucide-react'; 

const SideLink = ({ icon, label, active, onClick, count }) => (
  <button onClick={onClick} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
    active ? 'bg-indigo-50 text-indigo-700 shadow-sm border border-indigo-100' : 'text-slate-600 hover:bg-slate-50'
  }`}>
    <div className="flex items-center gap-3">
      {icon} <span>{label}</span>
    </div>
    {count !== undefined && count > 0 && (
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm ${active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
    )}
  </button>
);

const ClientKPI = ({ title, value, subtext, icon, alert }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 shadow-sm flex items-start justify-between">
    <div>
      <p className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-1 md:mb-2">{value}</h3>
      <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest">{subtext}</p>
    </div>
    <div className={`p-2.5 md:p-3 rounded-lg ${alert ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-600'}`}>
      {icon}
    </div>
  </div>
);

const ClientDashboard = ({ clientId: propClientId }) => {
  // 🛡️ THE SELF-HEALING ID STATE
  const [activeClientId, setActiveClientId] = useState(propClientId);

  const [alertNumber, setAlertNumber] = useState('');
  const [isSavingAlert, setIsSavingAlert] = useState(false);

  // 📡 CITATION RADAR STATES
  const [citationLinks, setCitationLinks] = useState({}); 
  const [isVerifyingCitation, setIsVerifyingCitation] = useState({});

  // 📧 PR OUTREACH STATES
  const [outreachTarget, setOutreachTarget] = useState("");
  const [outreachType, setOutreachType] = useState("COLLEGE");
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); 
  const [leads, setLeads] = useState([]);
  const [chats, setChats] = useState([]); 
  const [clientProfile, setClientProfile] = useState(null); 
  const [activeTab, setActiveTab] = useState('reports');
  
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); 
  const [sortOrder, setSortOrder] = useState("newest"); 
  
  const [pricing, setPricing] = useState(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('yearly'); 

  const [seoForm, setSeoForm] = useState({ 
      aiKnowledge: '', aiRules: '', keywords: '', gmbLink: '' 
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  const [toastMsg, setToastMsg] = useState(null); 
  const previousLeadsCount = useRef(0); 

  // 🔥 MARKET SPY STATES
  // 🎓 COURSES MANAGER STATES
  const [courses, setCourses] = useState([]);
  const [newCourse, setNewCourse] = useState('');
  const [isSavingCourses, setIsSavingCourses] = useState(false);

  const [compName, setCompName] = useState('');
  const [isAddingComp, setIsAddingComp] = useState(false);

  const playDingSound = () => {
    const audio = new Audio('https://actions.google.com/sounds/v1/alarms/beep_short.ogg');
    audio.play().catch(e => console.log("Audio play blocked"));
  };

  useEffect(() => {
    if (!activeClientId) {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          try {
            console.log("Self-healing triggered. Fetching clientId for:", user.uid);
            const userDoc = await getDoc(doc(db, "dashboard_users", user.uid));
            
            if (userDoc.exists() && userDoc.data().clientId) {
               setActiveClientId(userDoc.data().clientId);
            } else if (userDoc.exists() && userDoc.data().phone) {
               setActiveClientId(userDoc.data().phone);
            } else {
               const q = query(collection(db, "clients"), where("email", "==", user.email));
               const querySnapshot = await getDocs(q);
               if (!querySnapshot.empty) {
                  setActiveClientId(querySnapshot.docs[0].id);
               }
            }
          } catch (e) {
            console.error("Auto-fetch ID error:", e);
          }
        }
      });
      return () => unsubscribe();
    }
  }, [activeClientId]);

  useEffect(() => {
    if (!activeClientId) return;

    const unsubProfile = onSnapshot(doc(db, "clients", activeClientId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setClientProfile(data);
        setSeoForm({
          aiKnowledge: data.aiKnowledge || data.businessInfo || '',
          aiRules: data.aiRules || '',
          keywords: data.seoKeywords || '',
          gmbLink: data.gmbLink || ''
        });
        setAlertNumber(data.coordinator || '');
        setCourses(Array.isArray(data.courses) ? data.courses : []); 
      }
    });

    const unsubLeads = onSnapshot(query(collection(db, "clients", activeClientId, "leads"), orderBy("timestamp", "desc")), (snap) => {
        const fetchedLeads = snap.docs.map(doc => ({ id: doc.id, ...doc.data(), clientId: activeClientId }));
        setLeads(fetchedLeads);

        if (previousLeadsCount.current !== 0) {
            snap.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const newLd = change.doc.data();
                    const name = newLd.name || newLd.studentName || newLd.phone || "A new customer";
                    setToastMsg({
                        title: "🎉 New Lead Alert!",
                        desc: `${name} just connected.`,
                        time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                    });
                    playDingSound();
                    setTimeout(() => setToastMsg(null), 6000);
                }
            });
        }
        previousLeadsCount.current = fetchedLeads.length;
    });

    const unsubChats = onSnapshot(collection(db, "clients", activeClientId, "chats"), (snap) => {
        let fetchedChats = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        fetchedChats.sort((a, b) => {
            const timeA = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0);
            const timeB = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0);
            return timeB - timeA; 
        });
        setChats(fetchedChats);
    });

    const fetchPricing = async () => {
        const pDoc = await getDoc(doc(db, "system_config", "pricing"));
        if(pDoc.exists()) setPricing(pDoc.data());
    };
    fetchPricing();

    return () => { unsubLeads(); unsubChats(); unsubProfile(); };
  }, [activeClientId]);

  const saveSeoSettings = async (e) => {
    e.preventDefault();
    if (!activeClientId) return alert("System not ready.");
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "clients", activeClientId), { aiKnowledge: seoForm.aiKnowledge, aiRules: seoForm.aiRules, seoKeywords: seoForm.keywords, gmbLink: seoForm.gmbLink, businessInfo: seoForm.aiKnowledge });
      alert("✅ Configuration successfully updated!");
    } catch (error) { alert("⚠️ Error saving data."); }
    setIsSaving(false);
  };

  const handleSaveAlertNumber = async () => {
      if (!alertNumber || alertNumber.length < 10) {
          alert("Please enter a valid 10-digit WhatsApp number.");
          return;
      }
      setIsSavingAlert(true);
      try {
          let cleanNum = alertNumber.replace(/\D/g, "");
          if(cleanNum.length === 10) cleanNum = "91" + cleanNum;
          
          await updateDoc(doc(db, "clients", activeClientId), { coordinator: cleanNum });
          
          setToastMsg({ title: "✅ Success!", desc: "Alert number updated successfully.", time: "Now" });
      } catch (error) {
          alert("⚠️ Failed to save number.");
      }
      setIsSavingAlert(false);
  };

  // 🎓 COURSES HANDLERS
  const handleAddCourse = () => {
      const trimmed = newCourse.trim();
      if (!trimmed) return;
      if (trimmed.length > 24) return alert("Course name 24 characters se chota hona chahiye.");
      if (courses.includes(trimmed)) return alert("Yeh course pehle se hai.");
      if (courses.length >= 9) return alert("Maximum 9 courses ho sakte hain (+ Others auto-add hota hai).");
      setCourses([...courses, trimmed]);
      setNewCourse('');
  };

  const handleRemoveCourse = (index) => {
      setCourses(courses.filter((_, i) => i !== index));
  };

  const handleSaveCourses = async () => {
      if (!activeClientId) return;
      setIsSavingCourses(true);
      try {
          await updateDoc(doc(db, "clients", activeClientId), { courses });
          setToastMsg({ title: "✅ Courses Saved!", desc: "Bot mein naye courses update ho gaye.", time: "Now" });
      } catch (e) {
          alert("⚠️ Courses save nahi hue.");
      }
      setIsSavingCourses(false);
  };

 const enhanceKnowledgeWithAI = () => {
    setIsEnhancing(true);
    
    setTimeout(() => {
        let newText = seoForm.aiKnowledge;
        
        // 🧠 IF THE BOX IS EMPTY: Give them a ready-made template
        if (!seoForm.aiKnowledge.trim()) { 
            newText = `🏢 BUSINESS OVERVIEW:\nWe are [Your Business Name], located in [Your City]. We specialize in [Your Main Service/Product].\n\n💰 TOP SERVICES & PRICING:\n1. [Service 1 Name] - ₹[Price]\n2. [Service 2 Name] - ₹[Price]\n\n⏰ TIMINGS:\nMonday to Saturday: 10:00 AM - 7:00 PM\nSunday: Closed\n\n📍 ADDRESS:\n[Enter Your Full Address Here]\n\n🎯 SPECIAL OFFERS:\n- Free consultation/demo class for new customers.`;
        } 
        // 🧠 IF THEY WROTE SOMETHING: Structure their existing text
        else {
            newText = `🏢 BUSINESS OVERVIEW:\n${seoForm.aiKnowledge.trim()}\n\n💰 SERVICES:\n- Item 1: ₹[Price]\n\n⏰ TIMINGS:\n- 10 AM - 8 PM\n\n📍 ADDRESS:\n- Your City`;
        }
        
        setSeoForm({...seoForm, aiKnowledge: newText});
        setIsEnhancing(false);
    }, 800);
  };
  const toggleBotStatus = async () => {
      if (!activeClientId) return;
      const newStatus = clientProfile?.botPaused ? false : true;
      try { await updateDoc(doc(db, "clients", activeClientId), { botPaused: newStatus }); } catch (error) {}
  };

  const handleAddCompetitor = async (e) => {
    e.preventDefault();
    if (!compName || compName.trim() === "") {
        return alert("Bhai, Competitor ka naam aur city zaroori hai!");
    }
    if (!activeClientId) {
        return alert("❌ Error: Client ID is missing. Please refresh.");
    }
    
    setIsAddingComp(true);
    try {
      const clientRef = doc(db, "clients", activeClientId); 
      
      const newCompetitor = {
        id: Date.now().toString(),
        name: compName.trim(),
        addedAt: Date.now(),
        status: "PENDING"
      };

      const existingCompetitors = clientProfile?.competitors || [];
      
      await updateDoc(clientRef, {
        competitors: [...existingCompetitors, newCompetitor]
      });

      setCompName('');
      alert("✅ Target locked! Click 'Scan Now' to track them instantly.");
      
    } catch (error) {
      console.error("Lock Target Error:", error);
      alert(`❌ Error: ${error.message}`);
    }
    setIsAddingComp(false);
  };

  const handleRemoveCompetitor = async (competitorObj) => {
    if (!activeClientId) return;
    if (!window.confirm(`Stop tracking ${competitorObj.name}?`)) return;
    try {
      const clientRef = doc(db, "clients", activeClientId);
      const existingCompetitors = clientProfile?.competitors || [];
      const updatedCompetitors = existingCompetitors.filter(c => c.id !== competitorObj.id);
      
      await updateDoc(clientRef, {
        competitors: updatedCompetitors
      });
    } catch (error) {
      alert("❌ Error removing target.");
    }
  };

  const triggerInstantSpy = async () => {
    if (!activeClientId) return;
    alert("🔍 Spy Engine activated! Scanning Google Maps. Please wait 10 seconds...");
    try {
        await fetch(`https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/testSpyEngine?clientId=${activeClientId}`);
    } catch (e) {
        console.log("Scan trigger error:", e);
    }
  };

  const toggleAutoFestival = async () => {
    if (!activeClientId) return;
    const newStatus = clientProfile?.autoFestivalEnabled ? false : true;
    try { 
        await updateDoc(doc(db, "clients", activeClientId), { autoFestivalEnabled: newStatus }); 
        alert(newStatus ? "🎉 Auto-Festival Marketing Activated!" : "🛑 Auto-Festival Disabled.");
    } catch (error) {
        alert("Error updating status.");
    }
  };

  const initiatePayment = async (amount, planName) => {
      const finalAmount = amount || 0; 
      try {
          const functions = getFunctions();
          const getConfig = httpsCallable(functions, 'getPaymentConfig');
          const configRes = await getConfig();
          
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = () => {
              const options = {
                  key: configRes.data.keyId, amount: finalAmount * 100, currency: "INR",
                  name: "Pivot SaaS", description: planName,
                  handler: function (res) { alert(`✅ Success! ID: ${res.razorpay_payment_id}`); },
                  prefill: { name: clientProfile?.name, email: clientProfile?.email, contact: clientProfile?.whatsappId },
                  theme: { color: "#4f46e5" }
              };
              new window.Razorpay(options).open();
          };
          document.body.appendChild(script);
          setIsUpgradeModalOpen(false); 
      } catch (error) { alert("⚠️ Backend connection error."); }
  };

  const getLeadTime = (lead) => {
      if (lead.updatedAt?.toDate) return lead.updatedAt.toDate().getTime();
      if (lead.lastMessageAt?.toDate) return lead.lastMessageAt.toDate().getTime();
      if (lead.timestamp?.toDate) return lead.timestamp.toDate().getTime();
      if (lead.createdAt?.toDate) return lead.createdAt.toDate().getTime();
      return 0;
  };
  
  const processedLeads = leads.filter(lead => {
      const matchesSearch = (lead.name || lead.studentName || lead.phone || "").toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      const leadTime = getLeadTime(lead);
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;
      if (dateFilter === 'today') return (now - leadTime) < oneDay;
      if (dateFilter === 'week') return (now - leadTime) < 7 * oneDay;
      if (dateFilter === 'month') return (now - leadTime) < 30 * oneDay;
      return true;
  }).sort((a, b) => sortOrder === 'newest' ? getLeadTime(b) - getLeadTime(a) : getLeadTime(a) - getLeadTime(b));

  const tokensUsed = clientProfile?.tokensUsed || 0;
  const tokenQuota = clientProfile?.tokenQuota || 10000;
  const usagePercent = Math.min((tokensUsed / tokenQuota) * 100, 100).toFixed(0);
  const isHighUsage = usagePercent > 85;
  const isBotPaused = clientProfile?.botPaused === true;

  const updateLeadStatus = async (targetClientId, leadId, newStatus) => {
      if (!targetClientId) return;
      try {
          await updateDoc(doc(db, "clients", targetClientId, "leads", leadId), {
              status: newStatus,
              updatedAt: new Date()
          });
      } catch (error) {
          alert("Error updating status.");
      }
  };

  const handleEditName = async (targetClientId, leadId, currentName, e) => {
      e.stopPropagation(); 
      if (!targetClientId) return;
      const newName = window.prompt("Enter new name for lead:", currentName !== "New Prospect" ? currentName : "");
      if (newName && newName.trim() !== "") {
          try {
              await updateDoc(doc(db, "clients", targetClientId, "leads", leadId), {
                  studentName: newName.trim(),
                  whatsappName: newName.trim(),
                  updatedAt: new Date()
              });
          } catch (error) { alert("Name update error."); }
    }
  };

  // 🚀 CSV EXPORT FUNCTION
  const exportLeadsToCSV = () => {
    if (!processedLeads || processedLeads.length === 0) {
      alert("⚠️ Koi leads nahi mili export karne ke liye.");
      return;
    }

    const headers = ["Date", "Name", "Phone", "Interest/Course", "Status", "Source", "Last Message"];

    const csvRows = processedLeads.map(lead => {
      const leadTime = getLeadTime(lead);
      const dateStr = leadTime ? new Date(leadTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown';

      const name = lead.studentName || lead.whatsappName || lead.name || 'Unknown';
      const course = lead.course || lead.interestedIn || lead.query || 'General Inquiry';
      const msg = lead.messageText || lead.lastMessage || '';

      return [
        `"${dateStr}"`, 
        `"${name}"`, 
        `"${lead.phone || ''}"`, 
        `"${course}"`, 
        `"${lead.status || 'NEW'}"`,
        `"${lead.source || 'WhatsApp'}"`,
        `"${msg.replace(/"/g, '""')}"` // Escaping commas/quotes in messages
      ].join(','); 
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    
    const businessName = clientProfile?.name || 'Business';
    const fileName = `${businessName}_Leads_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.csv`;
    link.setAttribute("download", fileName.replace(/\s+/g, '_'));
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!activeClientId && !clientProfile) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-[#F8FAFC]">
        <div className="h-10 w-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Connecting Workspace...</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#F8FAFC] text-slate-800 font-sans selection:bg-indigo-100 overflow-hidden relative">
      
      {/* 📱 MOBILE OVERLAY */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 md:hidden backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* 🧊 SIDEBAR (Responsive) */}
      <aside className={`fixed md:relative inset-y-0 left-0 w-[260px] border-r border-slate-200 bg-white flex flex-col shadow-2xl md:shadow-sm z-50 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out`}>
        <div className="h-20 md:h-24 flex items-center px-4 md:px-6 border-b border-slate-100 justify-between">
          <div className="flex items-center gap-3 w-full">
            <div className={`p-2 rounded-xl shadow-md ${isBotPaused ? 'bg-amber-500' : 'bg-slate-900'}`}>
                <Bot size={20} className="text-white"/>
            </div>
            <div className="flex-1 overflow-hidden">
              <span className="font-black text-sm md:text-lg text-slate-900 tracking-tighter uppercase leading-none block truncate">
                {clientProfile?.name || 'SaaS Client'}
              </span>
              <button onClick={toggleBotStatus} className="flex items-center gap-1 mt-1 group">
                <div className={`w-7 md:w-8 h-3.5 md:h-4 rounded-full relative transition-colors ${isBotPaused ? 'bg-amber-200' : 'bg-emerald-200'}`}>
                    <div className={`absolute top-0.5 w-2.5 md:w-3 h-2.5 md:h-3 rounded-full bg-white shadow-sm transition-all ${isBotPaused ? 'left-0.5' : 'left-4 md:left-4.5 bg-emerald-500'}`}></div>
                </div>
                <span className={`text-[8px] md:text-[9px] font-bold uppercase tracking-widest ${isBotPaused ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {isBotPaused ? 'Paused' : 'Active'}
                </span>
              </button>
            </div>
          </div>
          <button className="md:hidden text-slate-400" onClick={() => setIsMobileMenuOpen(false)}><X size={20}/></button>
        </div>
        
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto custom-scrollbar">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-2">Workspace</p>
          <SideLink icon={<TrendingUp size={18}/>} label="Growth Report" active={activeTab === 'roi_report'} onClick={() => {setActiveTab('roi_report'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<BarChart3 size={18}/>} label="Bot Performance" active={activeTab === 'reports'} onClick={() => {setActiveTab('reports'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<MessageSquare size={18}/>} label="Live Inbox" active={activeTab === 'queries'} onClick={() => {setActiveTab('queries'); setIsMobileMenuOpen(false);}} count={chats.length} />
          <SideLink icon={<Database size={18}/>} label="Captured Leads" active={activeTab === 'leads'} onClick={() => {setActiveTab('leads'); setIsMobileMenuOpen(false);}} count={leads.length} />
          
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-8 mb-3 ml-2">Marketing</p>
          
          <SideLink icon={<Users size={18}/>} label="Audience CRM" active={activeTab === 'audience'} onClick={() => {setActiveTab('audience'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<QrCode size={18}/>} label="Marketing Hub" active={activeTab === 'marketing'} onClick={() => {setActiveTab('marketing'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Share2 size={18}/>} label="Social Autopilot" active={activeTab === 'social'} onClick={() => {setActiveTab('social'); setIsMobileMenuOpen(false);}} />

          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-8 mb-3 ml-2">Configuration</p>
          <SideLink icon={<BrainCircuit size={18}/>} label="AI Training & Spy" active={activeTab === 'seo'} onClick={() => {setActiveTab('seo'); setIsMobileMenuOpen(false);}} />
          <SideLink icon={<Settings size={18}/>} label="Settings & Billing" active={activeTab === 'settings'} onClick={() => {setActiveTab('settings'); setIsMobileMenuOpen(false);}} />
        </nav>

        <div className="p-4 border-t border-slate-100 bg-slate-50">
          <button onClick={() => signOut(auth)} className="w-full flex items-center justify-center md:justify-start gap-3 px-4 py-3 text-slate-500 hover:text-rose-600 rounded-xl font-bold text-sm transition-all hover:bg-white"><LogOut size={18}/> Secure Logout</button>
        </div>
      </aside>

      {/* 🚀 MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-10 w-full">
        <header className="h-16 md:h-20 px-4 md:px-8 flex items-center justify-between bg-white border-b border-slate-200 gap-2">
          
          <div className="flex items-center gap-2">
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-1.5 text-slate-500 rounded-lg hover:bg-slate-100">
              <Menu size={22} />
            </button>
          <h2 className="text-sm md:text-lg font-black text-slate-900 tracking-tight uppercase truncate max-w-[150px] sm:max-w-none">
              {activeTab === 'roi_report' ? 'Growth Report' : activeTab === 'reports' ? 'Performance' : activeTab === 'queries' ? 'Inbox' : activeTab === 'leads' ? 'Database' : activeTab === 'marketing' ? 'Marketing' : activeTab === 'settings' ? 'Settings' : activeTab === 'audience' ? 'Audience CRM' : activeTab === 'social' ? 'Social Autopilot' : 'AI Config'}
            </h2>
          </div>

          <div className="flex items-center gap-2 md:gap-4 shrink-0">
            <div onClick={() => setActiveTab('settings')} className="flex items-center gap-2 md:gap-3 px-2 md:px-4 py-1.5 md:py-2 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100">
               <Battery size={14} className={`md:w-4 md:h-4 ${isHighUsage ? "text-rose-500" : "text-emerald-500"}`} />
               <div>
                 <div className="w-12 md:w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden mb-0.5">
                    <div style={{width: `${usagePercent}%`}} className={`h-full ${isHighUsage ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                 </div>
                 <p className="text-[8px] md:text-[9px] font-black text-slate-500 uppercase tracking-widest hidden sm:block">{usagePercent}% Quota</p>
               </div>
            </div>
            <div className="bg-indigo-50 px-2 md:px-4 py-1 md:py-1.5 rounded-xl border border-indigo-100 flex flex-col items-end min-w-[70px]">
              <span className="text-[7px] md:text-[9px] font-black text-indigo-400 uppercase tracking-widest">Wallet</span>
              <span className="text-xs md:text-sm font-black text-indigo-700">₹{clientProfile?.walletBalance || 0}</span>
            </div>
          </div>
        </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
          <div className="max-w-[1600px] mx-auto">
            
            {/* 🚀 NEW TAB: GROWTH REPORT */}
            {activeTab === 'roi_report' && (
              <div className="animate-in fade-in duration-500">
                  <AnalyticsDashboard clientId={activeClientId} />
              </div>
            )}

            {/* 📊 Tab 1: Reports */}
            {activeTab === 'reports' && (
              <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  <ClientKPI title="Inquiries" value={chats.length} subtext="Chats" icon={<MessageSquare className="w-5 h-5 md:w-6 md:h-6" />} />
                  <ClientKPI title="Leads" value={leads.length} subtext="Captured" icon={<Database className="w-5 h-5 md:w-6 md:h-6" />} />
                  <ClientKPI title="Tokens" value={tokensUsed.toLocaleString()} subtext="Burned" icon={<Zap className="w-5 h-5 md:w-6 md:h-6" />} alert={isHighUsage} />
                  <ClientKPI title="Status" value={isBotPaused ? "Paused" : "Online"} subtext="System" icon={<Server className="w-5 h-5 md:w-6 md:h-6" />} alert={isBotPaused} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                  <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col">
                     <div className="px-4 md:px-6 py-4 md:py-5 border-b border-slate-100 bg-slate-50"><h3 className="text-[10px] md:text-xs font-black uppercase tracking-widest flex items-center gap-2"><ShieldCheck size={14}/> Bot Health</h3></div>
                     <div className="p-6 md:p-8 flex-1 flex flex-col items-center justify-center text-center space-y-4">
                        <div className="relative">
                          <div className={`p-4 md:p-6 rounded-full relative z-10 border ${isBotPaused ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                            {isBotPaused ? <Power size={36} className="text-amber-500 md:w-12 md:h-12" /> : <Bot size={36} className="text-emerald-600 md:w-12 md:h-12" />}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-black text-lg md:text-xl text-slate-900">{isBotPaused ? 'Bot is Paused' : 'Engine is Active'}</h4>
                          <p className="text-[10px] md:text-xs font-medium text-slate-500 mt-2 max-w-sm mx-auto">{isBotPaused ? 'Human takeover active.' : 'AI is handling customers autonomously.'}</p>
                        </div>
                        <button onClick={toggleBotStatus} className={`px-5 py-2.5 text-white rounded-xl text-[9px] md:text-[10px] font-black uppercase tracking-widest shadow-md ${isBotPaused ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'}`}>
                          {isBotPaused ? 'Resume AI' : 'Pause AI'}
                        </button>
                     </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col">
                    <div className="p-4 md:p-5 border-b border-slate-100 bg-slate-50"><h3 className="text-[10px] md:text-xs font-black text-slate-800 uppercase tracking-widest flex gap-2"><AlertTriangle size={14} /> Action Required</h3></div>
                    <div className="p-4 md:p-5">
                       {clientProfile?.walletBalance < 100 ? (
                         <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl">
                            <p className="text-[9px] md:text-[10px] font-black text-rose-700 uppercase tracking-widest mb-1">Low Balance</p>
                            <p className="text-[10px] md:text-xs font-medium text-rose-600 mb-3">Recharge WhatsApp wallet to continue.</p>
                            <button onClick={() => { setActiveTab('settings'); setTimeout(() => setIsUpgradeModalOpen(true), 100); }} className="w-full bg-rose-500 text-white text-[9px] md:text-[10px] font-bold py-2 rounded-lg uppercase">Recharge</button>
                         </div>
                       ) : (
                         <div className="p-4 border border-slate-100 bg-slate-50 rounded-xl text-center py-6 md:py-8">
                           <CheckCircle2 size={24} className="text-emerald-400 mx-auto mb-2 md:w-7 md:h-7" />
                           <p className="text-[10px] md:text-xs font-bold text-slate-600">All Good!</p>
                         </div>
                       )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 💬 Tab 2: Inbox */}
            {activeTab === 'queries' && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 animate-in fade-in duration-500">
                {chats.map(chat => (
                  <div key={chat.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <p className="font-bold text-sm">+{chat.id}</p>
                    <p className="text-xs text-slate-500 mt-1">Open Chat...</p>
                  </div>
                ))}
                {chats.length === 0 && <div className="col-span-full text-center py-20 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl text-xs md:text-sm">No active conversations.</div>}
              </div>
            )}

            {/* ⚙️ Tab 3: AI Training & Market Spy */}
            {activeTab === 'seo' && (
              <div className="max-w-4xl mx-auto animate-in fade-in duration-500 space-y-8">
                {/* BOT KNOWLEDGE SECTION */}
                <div className="bg-white border border-slate-200 p-5 md:p-8 rounded-2xl shadow-sm">
                  <div className="mb-6 md:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-slate-100 pb-4 md:pb-6 gap-4">
                    <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight uppercase flex items-center gap-2"><BrainCircuit className="w-5 h-5 text-indigo-600"/> AI Knowledge</h3>
                    <button onClick={saveSeoSettings} disabled={isSaving} className="w-full sm:w-auto bg-indigo-600 text-white font-black px-4 md:px-6 py-2 md:py-2.5 rounded-xl shadow-md text-[9px] md:text-[10px] uppercase tracking-widest flex justify-center">
                        {isSaving ? "Syncing..." : "Save & Deploy"}
                    </button>
                  </div>
                  <div className="bg-slate-50 p-4 md:p-6 rounded-2xl border border-slate-100">
                      <div className="flex flex-col sm:flex-row justify-between mb-3 md:mb-4 gap-2">
                          <h4 className="text-[10px] md:text-sm font-black text-slate-800 uppercase tracking-widest">Bot Training Data</h4>
                          <button type="button" onClick={enhanceKnowledgeWithAI} disabled={isEnhancing} className="bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-[9px] md:text-[10px] font-black uppercase">
                              {isEnhancing ? "Wait..." : "AI Structure"}
                          </button>
                      </div>
<textarea 
    rows="10" 
    value={seoForm.aiKnowledge} 
    onChange={(e) => setSeoForm({...seoForm, aiKnowledge: e.target.value})} 
    placeholder="Write a few words about your business here...\n\nOR simply click the 'AI STRUCTURE' button above to get a ready-made fill-in-the-blanks template!"
    className="w-full p-4 md:p-5 rounded-xl border border-slate-200 text-xs md:text-sm outline-none resize-none font-medium leading-relaxed bg-white shadow-inner focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition-all placeholder:text-slate-400"
></textarea>                  </div>
                </div>

                {/* 🎯 MARKET SPY ENGINE UI */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-8">
                  <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4 md:pb-6">
                    <div className="bg-rose-100 p-2 md:p-2.5 rounded-xl"><Crosshair className="w-5 h-5 md:w-6 md:h-6 text-rose-600"/></div>
                    <div>
                      <h2 className="text-lg md:text-xl font-black text-slate-900 uppercase tracking-tight">Market Spy Engine</h2>
                      <p className="text-[10px] md:text-xs font-medium text-slate-500 mt-1">Track competitors & receive AI optimization strategies via WhatsApp.</p>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    <div className="w-full md:w-1/3 bg-slate-50 p-4 md:p-5 rounded-xl border border-slate-100 h-fit">
                      <h3 className="text-xs font-bold text-slate-700 mb-4 uppercase tracking-widest flex items-center gap-2"><Search size={14} className="text-slate-400"/> Add Target</h3>
                      <form onSubmit={handleAddCompetitor} className="space-y-4">
                        <div>
                          <label className="text-[9px] md:text-[10px] font-bold text-slate-500 uppercase">Competitor Name + City</label>
                          <input type="text" value={compName} onChange={(e) => setCompName(e.target.value)} placeholder="e.g. Allen Career Dehradun" className="w-full mt-1.5 p-2.5 rounded-lg border border-slate-200 text-xs focus:ring-2 focus:ring-rose-100 outline-none" required />
                        </div>
                        <button type="submit" disabled={isAddingComp || (clientProfile?.competitors?.length >= 3)} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-lg text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-colors disabled:opacity-50">
                          {isAddingComp ? 'Adding...' : 'Lock Target'}
                        </button>
                        {clientProfile?.competitors?.length >= 3 && <p className="text-[9px] text-center text-rose-500 font-bold">Maximum 3 targets allowed.</p>}
                      </form>
                    </div>

                    <div className="w-full md:w-2/3">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-widest">Active Radar ({clientProfile?.competitors?.length || 0}/3)</h3>
                        {clientProfile?.competitors?.length > 0 && (
                          <button onClick={triggerInstantSpy} className="text-[9px] bg-indigo-100 text-indigo-700 hover:bg-indigo-600 hover:text-white font-black px-3 py-1.5 rounded-lg uppercase transition-all flex items-center gap-1 shadow-sm">
                            <Zap size={12}/> Scan Now
                          </button>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        {clientProfile?.competitors && clientProfile.competitors.length > 0 ? (
                          clientProfile.competitors.map((comp) => (
                            <div key={comp.id} className="flex justify-between items-center p-3 md:p-4 bg-white border border-rose-100 rounded-xl shadow-sm hover:border-rose-300 transition-colors group">
                               <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center border border-rose-100 text-rose-600">
                                      <Crosshair size={14} className={comp.status === 'FOUND' ? "" : "animate-pulse"} />
                                  </div>
                                  <div>
                                      <h4 className="text-sm font-bold text-slate-800 leading-tight">{comp.name}</h4>
                                      {comp.status === 'FOUND' ? (
                                          <p className="text-[10px] font-bold text-emerald-600 mt-0.5 flex items-center gap-1">
                                              ✅ Found • {comp.rating} ⭐ ({comp.reviews} Reviews)
                                          </p>
                                      ) : comp.status === 'NOT_FOUND' ? (
                                          <p className="text-[10px] font-bold text-rose-500 mt-0.5">❌ Not found on Maps</p>
                                      ) : (
                                          <p className="text-[9px] font-mono text-amber-500 mt-0.5">⏳ Waiting for scan...</p>
                                      )}
                                  </div>
                               </div>
                               <button onClick={() => handleRemoveCompetitor(comp)} className="text-slate-300 hover:text-rose-600 transition-colors p-2"><X size={16}/></button>
                            </div>
                          ))
                        ) : (
                          <div className="text-center p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center">
                              <Crosshair size={24} className="text-slate-300 mb-2"/>
                              <p className="text-xs font-bold text-slate-400">Radar is empty.</p>
                              <p className="text-[10px] font-medium text-slate-400 mt-1">Add a competitor to receive AI strategies.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 📡 AUTOMATED CITATION RADAR DETECTOR */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-6">
                    <div className="p-4 md:p-5 border-b border-slate-100 bg-gradient-to-r from-rose-50 to-white flex justify-between items-center">
                        <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            <Crosshair size={16} className="text-rose-600 animate-spin"/> Local Citation Radar (SEO Trust)
                        </h3>
                        <span className="text-[10px] font-black bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full uppercase">
                            Radar Scanning 📡
                        </span>
                    </div>
                    
                    <div className="p-5 space-y-4">
                        <p className="text-slate-500 text-xs font-medium mb-4 leading-relaxed">
                            Google algorithm aapke trust factor ko check karne ke liye niche di gayi directories se aapka data verify karta hai. Apne profiles banakar unka URL yahan submit karein:
                        </p>

                        <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden bg-slate-50">
                            {['Justdial', 'IndiaMART', 'Sulekha'].map((platform) => {
                                const savedData = clientProfile?.citations?.[platform] || {};
                                const currentStatus = savedData.status || 'PENDING';

                                return (
                                    <div key={platform} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white">
                                        <div className="w-full md:w-1/4">
                                            <h4 className="text-xs font-bold text-slate-800">{platform}</h4>
                                            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded mt-1 inline-block ${
                                                currentStatus === 'VERIFIED' ? 'bg-emerald-100 text-emerald-700' :
                                                currentStatus === 'MISMATCH' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                                            }`}>
                                                {currentStatus === 'VERIFIED' ? '✅ Verified' : currentStatus === 'MISMATCH' ? '❌ NAP Mismatch' : '⏳ Action Needed'}
                                            </span>
                                        </div>

                                        <div className="flex-1 flex gap-2">
                                            <input 
                                                type="text"
                                                placeholder={`Paste your ${platform} profile link here...`}
                                                defaultValue={savedData.url || ''}
                                                onChange={(e) => setCitationLinks({...citationLinks, [platform]: e.target.value})}
                                                className="w-full bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-xs outline-none focus:border-indigo-500 font-medium"
                                            />
                                            <button
                                                onClick={async () => {
                                                    const linkToVerify = citationLinks[platform] || savedData.url;
                                                    if(!linkToVerify) return alert("Pehle link paste karein!");
                                                    
                                                    setIsVerifyingCitation({...isVerifyingCitation, [platform]: true});
                                                    
                                                    try {
                                                        const response = await fetch('https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/verifyCitationRadar', {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ clientId: activeClientId, platform, url: linkToVerify })
                                                        });
                                                        const resData = await response.json();
                                                        alert(`Radar Scan Complete: Status is ${resData.status}`);
                                                    } catch(e) {
                                                        alert("Scan timeout! Link check background mein chal raha hai.");
                                                    }
                                                    
                                                    setIsVerifyingCitation({...isVerifyingCitation, [platform]: false});
                                                }}
                                                disabled={isVerifyingCitation[platform]}
                                                className="bg-slate-900 text-white font-bold px-4 py-2 rounded-xl text-[10px] uppercase tracking-widest hover:bg-indigo-600 transition-all shrink-0 disabled:opacity-50"
                                            >
                                                {isVerifyingCitation[platform] ? "Scanning..." : "Verify"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 🚀 DEEP BACKLINK & PR ENGINE CARD */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-6">
                    <div className="p-4 md:p-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white flex justify-between items-center">
                        <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            <Rocket size={16} className="text-emerald-600"/> PR & Deep Backlink Engine
                        </h3>
                        <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">
                            High Authority Links
                        </span>
                    </div>
                    
                    <div className="p-5 space-y-5">
                        <p className="text-slate-500 text-xs font-medium leading-relaxed">
                            Local Colleges, News Portals, aur Events se backlink lene ke liye AI se professional Pitch Email likhwayein. Ye links aapki ranking ko permanently boost karte hain.
                        </p>

                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="w-full md:w-1/3 space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase">Strategy Type</label>
                                    <select 
                                        value={outreachType} 
                                        onChange={(e) => setOutreachType(e.target.value)}
                                        className="w-full mt-1.5 p-2.5 rounded-lg border border-slate-200 text-xs focus:border-emerald-500 outline-none font-bold text-slate-700"
                                    >
                                        <option value="COLLEGE">🎓 College/School Workshop (.edu)</option>
                                        <option value="NEWS">📰 Local News Portal (Guest Post)</option>
                                        <option value="EVENT">🎪 Local Event Sponsorship</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase">Target Name</label>
                                    <input 
                                        type="text" 
                                        value={outreachTarget}
                                        onChange={(e) => setOutreachTarget(e.target.value)}
                                        placeholder="e.g. Graphic Era University" 
                                        className="w-full mt-1.5 p-2.5 rounded-lg border border-slate-200 text-xs focus:border-emerald-500 outline-none font-bold"
                                    />
                                </div>
                                <button 
                                    onClick={async () => {
                                        if(!outreachTarget) return alert("Please enter a target name!");
                                        setIsGeneratingEmail(true);
                                        try {
                                            const res = await fetch('https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/generateOutreachEmail', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ clientId: activeClientId, targetName: outreachTarget, strategyType: outreachType })
                                            });
                                            const data = await res.json();
                                            if(data.email) setGeneratedEmail(data.email);
                                        } catch(e) { alert("AI Error! Please try again."); }
                                        setIsGeneratingEmail(false);
                                    }}
                                    disabled={isGeneratingEmail}
                                    className="w-full bg-emerald-600 text-white font-black py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isGeneratingEmail ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Wand2 size={14}/>}
                                    Generate Pitch
                                </button>
                            </div>

                            <div className="w-full md:w-2/3 flex flex-col">
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">AI Generated Email Proposal</label>
                                <div className="flex-1 bg-white border border-slate-200 rounded-xl relative overflow-hidden focus-within:border-emerald-500 transition-colors shadow-sm">
                                    <textarea 
                                        value={generatedEmail}
                                        onChange={(e) => setGeneratedEmail(e.target.value)}
                                        readOnly={!generatedEmail}
                                        placeholder="Your AI generated outreach email will appear here..."
                                        className="w-full h-full min-h-[220px] p-4 text-xs md:text-sm text-slate-700 outline-none resize-none font-bold leading-relaxed bg-slate-50/30"
                                    />
                                    {generatedEmail && (
                                        <button 
                                            onClick={() => {
                                                navigator.clipboard.writeText(generatedEmail);
                                                setToastMsg({ title: "Copied!", desc: "Email copied to clipboard.", time: "Now" });
                                            }}
                                            className="absolute top-3 right-3 bg-white hover:bg-slate-100 text-slate-600 p-2 rounded-lg transition-colors border shadow-sm"
                                            title="Copy to Clipboard"
                                        >
                                            <Copy size={14}/>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

              </div>
            )}

            {/* 📬 Tab 4: Leads */}
            {activeTab === 'leads' && (
              <div className="space-y-4 md:space-y-6 animate-in fade-in duration-500">
                <div className="flex flex-col xl:flex-row justify-between gap-3 md:gap-4 bg-white p-3 md:p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 md:gap-3 bg-slate-50 px-3 md:px-4 py-2 rounded-xl border border-slate-100 w-full xl:w-1/3">
                        <Search size={14} className="text-slate-400 shrink-0" />
                        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-transparent border-none outline-none w-full text-xs md:text-sm" />
                    </div>
                    <div className="flex gap-2 w-full xl:w-auto">
                        <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-2.5 md:px-3 py-1.5 md:py-2 flex-1">
                            <Filter size={12} className="text-slate-400 mr-1 md:mr-2 shrink-0" />
                            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="bg-transparent outline-none text-[9px] md:text-xs font-bold text-slate-600 uppercase tracking-wider w-full">
                                <option value="all">All Time</option><option value="today">Today</option><option value="week">7 Days</option>
                            </select>
                        </div>
                       <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-2.5 md:px-3 py-1.5 md:py-2 flex-1">
                            <ArrowUpDown size={12} className="text-slate-400 mr-1 md:mr-2 shrink-0" />
                            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="bg-transparent outline-none text-[9px] md:text-xs font-bold text-slate-600 uppercase tracking-wider w-full">
                                <option value="newest">Newest</option><option value="oldest">Oldest</option>
                            </select>
                        </div>
                        
                        {/* 🚀 EXPORT BUTTON */}
                        <button 
                            onClick={exportLeadsToCSV}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200 rounded-xl px-3 py-1.5 md:py-2 flex items-center justify-center gap-1.5 transition-all shrink-0"
                            title="Download Leads to Excel"
                        >
                            <Download size={14} className="shrink-0"/>
                            <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider hidden sm:block">Export</span>
                        </button>

                    </div>
                </div>
              {/* 🚀 PREMIUM KANBAN PIPELINE VIEW */}
                <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 custom-scrollbar w-full h-full min-h-[60vh] snap-x">
                    {[
                        { id: 'NEW', title: '🟢 New & Active', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
                        { id: 'FOLLOW_UP', title: '⏳ Follow Up', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
                        { id: 'HOT', title: '🔥 Hot / Interested', bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700' },
                        { id: 'CLOSED', title: '🛑 Closed', bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-600' }
                    ].map(stage => (
                        <div key={stage.id} className="min-w-[280px] md:min-w-[320px] w-full max-w-[350px] flex flex-col bg-slate-50/70 rounded-2xl border border-slate-200 snap-center shadow-sm shrink-0">
                            
                            {/* Column Header */}
                            <div className={`px-4 py-3.5 border-b ${stage.border} ${stage.bg} rounded-t-2xl flex justify-between items-center sticky top-0 z-10`}>
                                <h3 className={`text-[11px] font-black uppercase tracking-widest ${stage.text}`}>{stage.title}</h3>
                                <span className={`text-[10px] font-bold bg-white px-2 py-0.5 rounded-full shadow-sm border ${stage.border} ${stage.text}`}>
                                    {processedLeads.filter(l => (l.status || 'NEW') === stage.id).length}
                                </span>
                            </div>
                            
                            {/* Lead Cards */}
                            <div className="flex-1 p-3 space-y-3 overflow-y-auto custom-scrollbar">
                                {processedLeads.filter(l => (l.status || 'NEW') === stage.id).length === 0 ? (
                                    <div className="text-center p-6 text-slate-400 text-[11px] font-semibold border-2 border-dashed border-slate-200 rounded-xl">Empty</div>
                                ) : (
                                    processedLeads.filter(l => (l.status || 'NEW') === stage.id).map(lead => {
                                        const displayName = lead.name || lead.studentName || lead.phone || "New Prospect";
                                        let shortMsg = lead.messageText || "Started conversation...";
                                        if (shortMsg.includes("Auto-Flow Triggered")) shortMsg = "Bot handled interaction";
                                        
                                        return (
                                            <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group hover:border-indigo-300">
                                                
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center font-black text-sm shrink-0">
                                                            {displayName.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-sm text-slate-800 line-clamp-1">{displayName}</h4>
                                                            <p className="text-[10px] font-semibold text-slate-500">+{lead.phone}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 mb-3 relative">
                                                    <MessageSquare size={12} className="text-slate-300 absolute top-2.5 right-2" />
                                                    <p className="text-[11px] text-slate-600 line-clamp-2 font-medium pr-4">"{shortMsg}"</p>
                                                </div>

                                                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                                                    <select 
                                                        value={lead.status || "NEW"}
                                                        onChange={(e) => updateLeadStatus(lead.clientId, lead.id, e.target.value)}
                                                        className="bg-white border border-slate-200 text-[9px] font-black uppercase text-slate-500 rounded px-1.5 py-1 outline-none focus:border-indigo-500 cursor-pointer shadow-sm hover:bg-slate-50"
                                                    >
                                                        <option value="NEW">Move ➔ New</option>
                                                        <option value="FOLLOW_UP">Move ➔ Follow Up</option>
                                                        <option value="HOT">Move ➔ Hot</option>
                                                        <option value="CLOSED">Move ➔ Closed</option>
                                                    </select>
                                                    <button className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded"><ExternalLink size={12}/> Open</button>
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>
                    ))}
                </div>              </div>
            )}

            {/* 🌟 Tab 5: Marketing & Festival Marketer */}
            {activeTab === 'marketing' && (
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8 animate-in fade-in duration-500">
                  <div className="w-full lg:w-1/3 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 text-center h-fit">
                      <h3 className="text-base md:text-lg font-black text-slate-900 tracking-tight mb-4">Magic Review QR</h3>
                      <div className="bg-slate-50 p-4 rounded-2xl flex justify-center mb-6 border border-slate-100">
                          <QRCodeSVG value={`https://pivot-rank.web.app/rate/${activeClientId}`} size={160} className="w-32 h-32 md:w-40 md:h-40" />
                      </div>
                  </div>

                  <div className="w-full lg:w-2/3 flex flex-col gap-6">
                      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                         <div className="p-5 md:p-6 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50 flex justify-between items-start md:items-center flex-col md:flex-row gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Calendar size={18} className="text-emerald-600" />
                                    <h3 className="text-sm md:text-base font-black text-emerald-900 uppercase tracking-widest">Viral Poster Engine</h3>
                                </div>
                                <p className="text-[10px] md:text-xs font-medium text-emerald-700">AI auto-generates & sends festival graphics to all your leads.</p>
                            </div>
                            
                            <label className="flex items-center cursor-pointer bg-white p-1.5 rounded-full shadow-sm border border-emerald-100">
                                <div className="relative">
                                    <input type="checkbox" className="sr-only" checked={clientProfile?.autoFestivalEnabled || false} onChange={toggleAutoFestival} />
                                    <div className={`block w-12 md:w-14 h-7 md:h-8 rounded-full transition-colors ${clientProfile?.autoFestivalEnabled ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
                                    <div className={`dot absolute left-1 top-1 bg-white w-5 md:w-6 h-5 md:h-6 rounded-full transition-transform ${clientProfile?.autoFestivalEnabled ? 'transform translate-x-5 md:translate-x-6' : ''}`}></div>
                                </div>
                                <div className="ml-3 mr-2 text-[10px] md:text-xs font-black uppercase text-slate-600">Auto-Pilot</div>
                            </label>
                         </div>
                         
                         <div className="p-5 md:p-6">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Upcoming Marketing Calendar</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex items-center p-3 border border-slate-200 rounded-xl bg-slate-50">
                                    <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center shrink-0 mr-3"><ImageIcon size={18}/></div>
                                    <div className="flex-1">
                                        <h4 className="text-xs font-bold text-slate-800">Independence Day</h4>
                                        <p className="text-[9px] text-slate-400 mt-0.5">Aug 15 • Auto-Blast</p>
                                    </div>
                                    {clientProfile?.autoFestivalEnabled ? <CheckCircle2 size={16} className="text-emerald-500"/> : <Clock size={16} className="text-slate-300"/>}
                                </div>
                                <div className="flex items-center p-3 border border-slate-200 rounded-xl bg-slate-50">
                                    <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center shrink-0 mr-3"><ImageIcon size={18}/></div>
                                    <div className="flex-1">
                                        <h4 className="text-xs font-bold text-slate-800">Diwali Special</h4>
                                        <p className="text-[9px] text-slate-400 mt-0.5">Nov 12 • Auto-Blast</p>
                                    </div>
                                    {clientProfile?.autoFestivalEnabled ? <CheckCircle2 size={16} className="text-emerald-500"/> : <Clock size={16} className="text-slate-300"/>}
                                </div>
                            </div>
                         </div>
                      </div>

                      <div className="bg-slate-900 rounded-2xl p-6 shadow-xl flex flex-col sm:flex-row justify-between text-white gap-4 items-center">
                          <div>
                              <h3 className="text-lg md:text-xl font-black mb-1">Premium Review Standee</h3>
                              <p className="text-[10px] md:text-xs text-slate-400">Boost your Google rankings offline. Delivered to your door.</p>
                          </div>
                          <button onClick={() => initiatePayment(499, 'Standee')} className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 transition-colors font-black px-6 py-3 rounded-xl uppercase text-[10px] md:text-xs shadow-lg">Order for ₹499</button>
                      </div>
                  </div>
              </div>
            )}

            {/* 🔥 NEW TAB: AUDIENCE CRM 🔥 */}
            {activeTab === 'audience' && (
                <div className="animate-in fade-in duration-500">
                    <ClientAudience 
                        clientId={activeClientId} 
                        onRecharge={() => setIsUpgradeModalOpen(true)} 
                    />
                </div>
            )}

            {/* 📱 Tab: Social Autopilot */}
            {activeTab === 'social' && (
                <div className="animate-in fade-in duration-500">
                    <SocialAutopilot tenants={activeClientId ? [{ 
                        id: activeClientId, 
                        name: clientProfile?.name || 'My Business', 
                        category: clientProfile?.category || clientProfile?.businessCandidate?.category || 'Business',
                        fbPageToken: clientProfile?.fbPageToken,
                        metaToken: clientProfile?.metaToken,
                        fbPageId: clientProfile?.fbPageId
                    }] : []} />
                </div>
            )}

            {/* ⚙️ Tab 6: Settings */}
            {activeTab === 'settings' && (
              <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 animate-in fade-in duration-500">
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm">
                      <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest mb-4 md:mb-6 border-b pb-3 md:pb-4">Business Profile</h3>
                      <div className="space-y-3 md:space-y-4">
                          <div><label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase">Name</label><input disabled value={clientProfile?.name || ''} className="w-full bg-slate-50 border border-slate-200 px-3 py-2 md:px-4 md:py-3 rounded-xl text-xs md:text-sm font-bold" /></div>
                          <div><label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase">WhatsApp ID</label><input disabled value={clientProfile?.whatsappId || ''} className="w-full bg-slate-50 border border-slate-200 px-3 py-2 md:px-4 md:py-3 rounded-xl text-xs md:text-sm font-mono" /></div>
                          
                          {/* 🔥 NAYA ALERT NUMBER CARD 🔥 */}
                          <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm mt-6">
                              <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest mb-2 flex items-center gap-2">
                                  <Bell size={16} className="text-amber-500"/> Lead Alert Number
                              </h3>
                              <p className="text-[10px] md:text-xs text-slate-500 font-medium mb-4">
                                  Google Maps se aane wali nayi leads ka WhatsApp notification is number par jayega. (Customers ko ye nahi dikhega).
                              </p>
                              <div className="flex flex-col sm:flex-row gap-3">
                                  <input 
                                      type="text"
                                      value={alertNumber}
                                      onChange={(e) => setAlertNumber(e.target.value)}
                                      placeholder="Enter WhatsApp No (e.g. 9876543210)" 
                                      className="w-full bg-slate-50 border border-slate-200 px-3 py-2 md:px-4 md:py-3 rounded-xl text-xs md:text-sm font-bold outline-none focus:border-indigo-500" 
                                  />
                                  <button 
                                      onClick={handleSaveAlertNumber}
                                      disabled={isSavingAlert}
                                      className="bg-slate-900 text-white font-black px-6 py-2 md:py-3 rounded-xl text-[10px] md:text-xs uppercase tracking-widest hover:bg-slate-800 transition-colors shrink-0 disabled:opacity-50 flex items-center justify-center gap-2"
                                  >
                                      {isSavingAlert ? "Saving..." : <><Save size={14}/> Save Number</>}
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>
                  <div className="space-y-4 md:space-y-6">
                      <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-5 md:p-6 shadow-xl text-white">
                          <h3 className="text-[10px] md:text-xs font-bold text-indigo-300 uppercase tracking-widest mb-1">Plan</h3>
                          <h2 className="text-3xl md:text-4xl font-black mb-4 md:mb-6">{clientProfile?.planType || 'Free'}</h2>
                          <button onClick={() => setIsUpgradeModalOpen(true)} className="w-full bg-indigo-500 font-black py-3 md:py-4 rounded-xl uppercase text-[9px] md:text-[10px]">Upgrade / Top-up</button>
                      </div>
                      <div className="bg-white border border-emerald-200 rounded-2xl p-5 md:p-6 flex justify-between items-center shadow-sm">
                          <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest">WhatsApp Credits</h3>
                          <button onClick={() => setIsUpgradeModalOpen(true)} className="bg-emerald-50 text-emerald-700 px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-bold text-[10px] md:text-xs flex items-center gap-2 border border-emerald-200 transition-all hover:bg-emerald-100">Recharge <ArrowUpRight size={14}/></button>
                      </div>
                  </div>

                  {/* 🎓 COURSES MANAGER */}
                  <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm">
                      <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest mb-1 flex items-center gap-2">
                          <BrainCircuit size={16} className="text-indigo-500"/> WhatsApp Bot — Course List
                      </h3>
                      <p className="text-[10px] text-slate-400 font-medium mb-4">Yeh courses WhatsApp bot mein students ko dikhenge. Max 9 courses (+ Others auto hota hai).</p>
                      <div className="flex flex-wrap gap-2 mb-4 min-h-[44px]">
                          {courses.map((course, i) => (
                              <div key={i} className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-bold">
                                  {course}
                                  <button onClick={() => handleRemoveCourse(i)} className="text-indigo-300 hover:text-red-500 transition-colors ml-1"><X size={12}/></button>
                              </div>
                          ))}
                          {courses.length === 0 && <p className="text-[10px] text-slate-400 font-medium">Koi course nahi hai. Neeche add karein.</p>}
                      </div>
                      <div className="flex gap-2 mb-4">
                          <input
                              type="text"
                              value={newCourse}
                              onChange={(e) => setNewCourse(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddCourse()}
                              placeholder="Course name likhein (max 24 chars)..."
                              maxLength={24}
                              className="flex-1 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl text-xs font-bold outline-none focus:border-indigo-500"
                          />
                          <button onClick={handleAddCourse} className="bg-indigo-600 text-white font-black px-4 py-2.5 rounded-xl text-xs uppercase hover:bg-indigo-700 transition-colors shrink-0">+ Add</button>
                      </div>
                      <button onClick={handleSaveCourses} disabled={isSavingCourses} className="w-full bg-slate-900 text-white font-black py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                          {isSavingCourses ? "Saving..." : <><Save size={14}/> Save Courses</>}
                      </button>
                  </div>
              </div>
            )}

          </div>
        </main>
      </div>

      {/* 🍞 TOAST NOTIFICATION POPUP */}
      {toastMsg && (
          <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 bg-slate-900 text-white p-4 rounded-2xl shadow-2xl z-[500] animate-in slide-in-from-bottom-5 fade-in duration-300 flex items-start gap-4 border border-slate-700 min-w-[280px]">
              <div className="bg-indigo-500/20 p-2 rounded-xl text-indigo-400 shrink-0">
                  <Bell size={20} className="animate-pulse" />
              </div>
              <div className="flex-1">
                  <h4 className="text-[10px] md:text-xs font-black uppercase tracking-widest text-indigo-400">{toastMsg.title}</h4>
                  <p className="text-xs md:text-sm text-slate-200 font-medium mt-1">{toastMsg.desc}</p>
                  <p className="text-[9px] text-slate-500 mt-2 font-mono">{toastMsg.time}</p>
              </div>
              <button onClick={() => setToastMsg(null)} className="text-slate-500 hover:text-white transition-colors shrink-0">
                  <X size={16} />
              </button>
          </div>
      )}

      {/* 💎 UPGRADE & RECHARGE MODAL */}
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 md:p-4 animate-in fade-in">
          <div className="bg-[#F8FAFC] rounded-2xl md:rounded-3xl w-full max-w-5xl overflow-hidden shadow-2xl flex flex-col max-h-[95vh]">
            <div className="p-4 md:p-6 flex justify-between items-center bg-white border-b shrink-0">
              <h3 className="text-base md:text-xl font-black uppercase tracking-widest text-slate-800">Store & Billing</h3>
              <button onClick={() => setIsUpgradeModalOpen(false)} className="bg-slate-100 p-1.5 md:p-2 rounded-full hover:bg-rose-100 hover:text-rose-600 transition-colors"><X size={18}/></button>
            </div>
            
            <div className="p-4 md:p-6 overflow-y-auto custom-scrollbar space-y-8">
                <div>
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <Zap size={16} className="text-amber-500"/> Blast Credit Packs
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center shadow-sm hover:border-indigo-300 transition-all flex flex-col">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase mb-2">Starter Pack</h4>
                            <span className="text-2xl font-black text-slate-800 mb-1">₹{pricing?.recharge_starter_price || 500}</span>
                            <p className="text-[10px] font-bold text-emerald-600 mb-4 bg-emerald-50 py-1 rounded-md">Get {pricing?.recharge_starter_credits || 500} Credits</p>
                            <button onClick={() => initiatePayment(pricing?.recharge_starter_price || 500, 'Blast Credits - Starter')} className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 text-[10px] font-black uppercase mt-auto hover:bg-slate-50 transition-colors">Recharge</button>
                        </div>
                        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5 relative shadow-xl text-center flex flex-col transform md:scale-105 z-10">
                            <div className="absolute -top-3 inset-x-4 bg-indigo-500 text-white text-[8px] font-black text-center py-1 rounded-full uppercase shadow-md flex justify-center items-center gap-1"><Zap size={10}/> Most Popular</div>
                            <h4 className="text-[10px] font-black text-indigo-300 uppercase mb-2 mt-2">Value Pack</h4>
                            <span className="text-3xl font-black text-white mb-1">₹{pricing?.recharge_value_price || 1000}</span>
                            <p className="text-[10px] font-bold text-emerald-400 mb-4 bg-emerald-400/10 py-1 rounded-md">+ Bonus ({pricing?.recharge_value_credits || 1150} Credits)</p>
                            <button onClick={() => initiatePayment(pricing?.recharge_value_price || 1000, 'Blast Credits - Value Pack')} className="w-full py-3 rounded-xl bg-indigo-500 text-white text-[10px] font-black uppercase mt-auto shadow-lg hover:bg-indigo-400 transition-colors">Recharge</button>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center shadow-sm hover:border-indigo-300 transition-all flex flex-col">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase mb-2">Pro Pack</h4>
                            <span className="text-2xl font-black text-slate-800 mb-1">₹{pricing?.recharge_pro_price || 3000}</span>
                            <p className="text-[10px] font-bold text-emerald-600 mb-4 bg-emerald-50 py-1 rounded-md">+ Mega Bonus ({pricing?.recharge_pro_credits || 3600} Credits)</p>
                            <button onClick={() => initiatePayment(pricing?.recharge_pro_price || 3000, 'Blast Credits - Pro Pack')} className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 text-[10px] font-black uppercase mt-auto hover:bg-slate-50 transition-colors">Recharge</button>
                        </div>
                    </div>
                </div>

                <hr className="border-slate-200" />

                <div>
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center justify-between">
                        <span className="flex items-center gap-2"><Crown size={16} className="text-indigo-500"/> Upgrade Plan</span>
                        
                        <div className="bg-slate-200 p-1 rounded-lg flex gap-1 shadow-inner">
                            <button onClick={() => setBillingCycle('quarterly')} className={`px-3 sm:px-4 py-1.5 rounded-md text-[8px] sm:text-[9px] font-black uppercase ${billingCycle === 'quarterly' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}>Quarterly</button>
                            <button onClick={() => setBillingCycle('yearly')} className={`px-3 sm:px-4 py-1.5 rounded-md text-[8px] sm:text-[9px] font-black uppercase flex items-center gap-1 ${billingCycle === 'yearly' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Yearly <span className="bg-amber-400 text-amber-900 px-1 rounded text-[6px] hidden sm:block">Save 40%</span></button>
                        </div>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center flex flex-col">
                            <h3 className="text-[10px] font-black text-slate-500 uppercase mb-2">Starter</h3>
                            <div className="mb-6"><span className="text-2xl font-black">₹{billingCycle === 'yearly' ? (pricing?.starter_y || 4999) : (pricing?.starter_q || 1499)}</span></div>
                            <button onClick={() => initiatePayment(billingCycle === 'yearly' ? (pricing?.starter_y || 4999) : (pricing?.starter_q || 1499), 'Starter Plan')} className="w-full py-3 rounded-xl border-2 text-[9px] font-black uppercase mt-auto hover:bg-slate-50">Buy Starter</button>
                        </div>
                        <div className="bg-indigo-900 rounded-2xl p-5 relative shadow-xl text-center flex flex-col">
                            <div className="absolute -top-3 inset-x-4 bg-indigo-500 text-white text-[8px] font-black py-1 rounded-full uppercase">Most Popular 🔥</div>
                            <h3 className="text-[10px] font-black text-indigo-300 uppercase mb-2 mt-2">Growth</h3>
                            <div className="mb-6"><span className="text-3xl font-black text-white">₹{billingCycle === 'yearly' ? (pricing?.growth_y || 12999) : (pricing?.growth_q || 3999)}</span></div>
                            <button onClick={() => initiatePayment(billingCycle === 'yearly' ? (pricing?.growth_y || 12999) : (pricing?.growth_q || 3999), 'Growth Plan')} className="w-full py-3 rounded-xl bg-indigo-500 text-white text-[9px] font-black uppercase mt-auto hover:bg-indigo-400">Buy Growth</button>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center flex flex-col">
                            <h3 className="text-[10px] font-black text-slate-500 uppercase mb-2">Full Suite</h3>
                            <div className="mb-6"><span className="text-2xl font-black">₹{billingCycle === 'yearly' ? (pricing?.suite_y || 24999) : (pricing?.suite_q || 7499)}</span></div>
                            <button onClick={() => initiatePayment(billingCycle === 'yearly' ? (pricing?.suite_y || 24999) : (pricing?.suite_q || 7499), 'Full Suite')} className="w-full py-3 rounded-xl bg-slate-900 text-white text-[9px] font-black uppercase mt-auto hover:bg-black">Buy Suite</button>
                        </div>
                    </div>
                </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;